import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Trash2, RotateCcw, Loader2, Users, GraduationCap, Building2, RefreshCw, AlertTriangle, MailOpen } from 'lucide-react';

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const Section = ({ icon: Icon, title, items, renderRow, type, onRestore, onPurge }) => (
  <div className="bg-white border border-[#0B5E2A]/10 rounded-xl overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3 border-b border-[#0B5E2A]/10 bg-[#f0f6ee]">
      <div className="flex items-center gap-2 text-[#0B5E2A]">
        <Icon className="h-4 w-4" />
        <div className="font-display text-base font-semibold">{title}</div>
        <span className="inline-flex items-center h-6 px-2 rounded-full bg-white text-[#0B5E2A] border border-[#0B5E2A]/20 text-xs font-bold">{items.length}</span>
      </div>
    </div>
    {items.length === 0 ? (
      <div className="py-10 text-center text-sm text-[#6b7e6d]">Nothing here.</div>
    ) : (
      <ul className="divide-y divide-[#0B5E2A]/10" data-testid={`trash-list-${type}`}>
        {items.map((it) => (
          <li key={it.id} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-[#faf8ee]">
            <div className="min-w-0 flex-1">{renderRow(it)}</div>
            <div className="text-xs text-[#6b7e6d] whitespace-nowrap hidden md:block" title={`Deleted by ${it.deleted_by || 'admin'}`}>Deleted {fmtDate(it.deleted_at)}</div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => onRestore(type, it.id, it._label)} data-testid={`restore-${type}-${it.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded bg-[#E1F3E4] text-[#0B5E2A] hover:bg-[#c9e7ce] transition-colors">
                <RotateCcw className="h-3.5 w-3.5" /> Restore
              </button>
              <button onClick={() => onPurge(type, it.id, it._label)} data-testid={`purge-${type}-${it.id}`} title="Permanently delete" className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded bg-[#FDE2E2] text-[#9b1c1c] hover:bg-[#fbcfcf] transition-colors">
                <Trash2 className="h-3.5 w-3.5" /> Purge
              </button>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const TrashTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ recruiters: [], students: [], colleges: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/trash');
      setData(data);
    } catch (e) { /* 401 global */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRestore = async (type, id, label) => {
    try {
      await api.post('/admin/trash/restore', { type, id });
      toast({ title: 'Restored', description: label });
      load();
    } catch (e) { toast({ title: 'Restore failed', description: e?.response?.data?.detail || 'Error' }); }
  };

  const onPurge = async (type, id, label) => {
    if (!window.confirm(`Permanently delete "${label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/trash/${type}/${id}`);
      toast({ title: 'Purged', description: label });
      load();
    } catch (e) { toast({ title: 'Purge failed', description: e?.response?.data?.detail || 'Error' }); }
  };

  const emptyAll = async () => {
    if (!window.confirm('Permanently delete ALL trashed items? This cannot be undone.')) return;
    try {
      const { data: res } = await api.post('/admin/trash/empty');
      const p = res.purged || {};
      toast({ title: 'Trash emptied', description: `${p.recruiters || 0} recruiters · ${p.students || 0} students · ${p.colleges || 0} colleges · ${p.applications || 0} leads` });
      load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || 'Error' }); }
  };

  if (loading) {
    return <div className="py-16 text-center text-[#6b7e6d]" data-testid="trash-loading"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>;
  }

  const total = data.recruiters.length + data.students.length + data.colleges.length + (data.applications?.length || 0);

  const recs = data.recruiters.map((r) => ({ ...r, _label: r.name }));
  const studs = data.students.map((s) => ({ ...s, _label: s.full_name }));
  const colls = data.colleges.map((c) => ({ ...c, _label: c.name }));
  const apps = (data.applications || []).map((a) => ({ ...a, _label: a.name }));

  return (
    <div className="space-y-4" data-testid="trash-tab">
      <div className="flex items-start gap-3 bg-[#FEF4DB] border border-[#F0B93C]/50 rounded-xl px-5 py-4">
        <AlertTriangle className="h-5 w-5 text-[#8a5e00] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-base text-[#8a5e00]">Trash</div>
          <p className="text-sm text-[#6b7e6d] mt-1">Items you delete from the Recruiters, Students and Colleges tabs land here first. Restore them any time, or purge them to free up storage.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} title="Refresh" className="h-9 w-9 rounded border border-[#0B5E2A]/20 text-[#0B5E2A] hover:bg-[#f0f6ee] flex items-center justify-center"><RefreshCw className="h-4 w-4" /></button>
          <button onClick={emptyAll} disabled={total === 0} data-testid="empty-trash-btn" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#9b1c1c] text-white text-sm font-semibold hover:bg-[#7a1515] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            <Trash2 className="h-3.5 w-3.5" /> Empty Trash
          </button>
        </div>
      </div>

      <Section
        icon={Users}
        title="Recruiters"
        items={recs}
        type="recruiter"
        onRestore={onRestore}
        onPurge={onPurge}
        renderRow={(r) => (
          <div>
            <div className="font-semibold text-[#0B5E2A] truncate">{r.name}</div>
            <div className="text-xs text-[#6b7e6d] truncate">{r.email} · {r.company} · {r.country}</div>
          </div>
        )}
      />

      <Section
        icon={GraduationCap}
        title="Students"
        items={studs}
        type="student"
        onRestore={onRestore}
        onPurge={onPurge}
        renderRow={(s) => (
          <div>
            <div className="font-semibold text-[#0B5E2A] truncate">{s.full_name}</div>
            <div className="text-xs text-[#6b7e6d] truncate">{s.email} · {s.nationality || '—'} · stage: {s.application_stage || 'draft'}</div>
          </div>
        )}
      />

      <Section
        icon={Building2}
        title="Colleges"
        items={colls}
        type="college"
        onRestore={onRestore}
        onPurge={onPurge}
        renderRow={(c) => (
          <div>
            <div className="font-semibold text-[#0B5E2A] truncate">{c.name}</div>
            <div className="text-xs text-[#6b7e6d] truncate">{c.location}, {c.country} · {c.program_count || 0} programs</div>
          </div>
        )}
      />

      <Section
        icon={MailOpen}
        title="Direct Leads"
        items={apps}
        type="application"
        onRestore={onRestore}
        onPurge={onPurge}
        renderRow={(a) => (
          <div>
            <div className="font-semibold text-[#0B5E2A] truncate">{a.name}</div>
            <div className="text-xs text-[#6b7e6d] truncate">{a.email} · {a.nationality || '—'} · status: {a.status || 'pending'}</div>
          </div>
        )}
      />
    </div>
  );
};

export default TrashTab;
